import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { buildMrpSkuDetailPath } from "@/lib/mrp";
import {
  createLabelSku,
  listArtFileOptions,
  listLabelSkus,
  listPotentialLabelSkus,
  updateLabelSku,
  type ArtFileOption,
  type LabelSkuRow,
} from "@/lib/artAndLabelsApi";

export function LabelSkusPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [labelSku, setLabelSku] = useState("");
  const [productSku, setProductSku] = useState("");
  const [artId, setArtId] = useState<number | "">("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const skusQuery = useQuery({
    queryKey: ["art-and-labels", "label-skus"],
    queryFn: listLabelSkus,
  });
  const potentialQuery = useQuery({
    queryKey: ["art-and-labels", "label-skus", "potential"],
    queryFn: listPotentialLabelSkus,
    enabled: modalOpen,
  });
  const artOptionsQuery = useQuery({
    queryKey: ["art-and-labels", "label-skus", "art-options"],
    queryFn: listArtFileOptions,
  });

  const artOptions = artOptionsQuery.data ?? [];
  const artOptionsById = useMemo(() => {
    const map = new Map<number, ArtFileOption>();
    for (const option of artOptions) {
      map.set(option.value, option);
    }
    return map;
  }, [artOptions]);

  const updateMutation = useMutation({
    mutationFn: (input: { labelSku: string; active: boolean; artId: number }) => updateLabelSku(input.labelSku, input),
    onSuccess: async () => {
      setStatusMessage("Label SKU updated.");
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ["art-and-labels", "label-skus"] });
    },
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const createMutation = useMutation({
    mutationFn: createLabelSku,
    onSuccess: async () => {
      setStatusMessage("Label SKU created.");
      setErrorMessage(null);
      setModalOpen(false);
      setLabelSku("");
      setProductSku("");
      setArtId("");
      await queryClient.invalidateQueries({ queryKey: ["art-and-labels", "label-skus"] });
      await queryClient.invalidateQueries({ queryKey: ["art-and-labels", "label-skus", "potential"] });
    },
    onError: (error: Error) => setErrorMessage(error.message),
  });

  function openEditSkuPage(row: LabelSkuRow) {
    const url = buildMrpSkuDetailPath(row.product_sku);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const rows = skusQuery.data ?? [];

  const columns: HubDataTableColumn<LabelSkuRow>[] = useMemo(
    () => [
      {
        id: "labelSku",
        header: "Label SKU",
        overflow: "nowrap",
        sortValue: (row) => row.label_sku,
        cell: (row) => row.label_sku,
      },
      {
        id: "productSku",
        header: "Product SKU",
        overflow: "nowrap",
        sortValue: (row) => row.product_sku,
        cell: (row) => row.product_sku,
      },
      {
        id: "productName",
        header: "Product name",
        sortValue: (row) => row.product_name ?? "",
        cell: (row) => row.product_name ?? "—",
      },
      {
        id: "serialCode",
        header: "Serial code",
        overflow: "nowrap",
        sortValue: (row) => row.bar_code_model ?? "",
        cell: (row) => row.bar_code_model ?? "—",
      },
      {
        id: "active",
        header: "Active",
        overflow: "nowrap",
        sortValue: (row) => (row.active ? 1 : 0),
        cell: (row) => (
          <input
            type="checkbox"
            checked={row.active}
            onChange={(event) => {
              if (row.art_id == null) {
                return;
              }
              updateMutation.mutate({
                labelSku: row.label_sku,
                active: event.target.checked,
                artId: row.art_id,
              });
            }}
            aria-label={`Active for ${row.label_sku}`}
          />
        ),
      },
      {
        id: "artName",
        header: "Art name",
        sortValue: (row) => artOptionsById.get(row.art_id ?? -1)?.label ?? "",
        cell: (row) => (
          <select
            className="w-full min-w-[12rem] rounded border border-slate-200 px-2 py-1"
            value={row.art_id ?? ""}
            onChange={(event) => {
              const nextArtId = Number(event.target.value);
              if (!Number.isInteger(nextArtId)) {
                return;
              }
              updateMutation.mutate({
                labelSku: row.label_sku,
                active: row.active,
                artId: nextArtId,
              });
            }}
          >
            <option value="" disabled>
              Select art
            </option>
            {artOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ),
      },
      {
        id: "artType",
        header: "Art type",
        overflow: "nowrap",
        sortValue: (row) => row.label_type ?? "",
        cell: (row) => row.label_type ?? "—",
      },
      {
        id: "actions",
        header: "Actions",
        overflow: "nowrap",
        sortValue: () => "",
        cell: (row) => (
          <Button type="button" variant="ghost" size="sm" onClick={() => openEditSkuPage(row)}>
            Edit SKU
          </Button>
        ),
      },
    ],
    [artOptions, artOptionsById, updateMutation],
  );

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Map label SKUs to product SKUs and art files. Toggle active or change art inline. Use Edit SKU to set the label bar code number on the product SKU in MRP.">
        Label SKUs
      </PageTitleWithHelp>

      {statusMessage ? <p className="text-sm text-emerald-700">{statusMessage}</p> : null}
      {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}

      <HubDataTable
        title="Label SKU mappings"
        headerExtra={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(true)}>
              Create new label
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => window.open("/mrp/skus", "_blank", "noopener,noreferrer")}
              title="Opens MRP SKUs in a new tab. Find the PRODUCT SKU (not the label SKU) and ensure a Label Bar Code Number is set."
            >
              Navigate to Edit SKU page
            </Button>
          </div>
        }
        columns={columns}
        rows={rows}
        rowKey={(row) => row.label_sku}
        loading={skusQuery.isLoading}
        loadingMessage="Loading…"
        empty="No label SKUs."
        tableClassName="min-w-[56rem]"
      />

      {modalOpen ? (
        <DocumentModal title="Create new label SKU" onClose={() => setModalOpen(false)} showCloseButton={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Label SKU</span>
              <select
                className="w-full rounded border border-slate-200 px-3 py-2"
                value={labelSku}
                onChange={(event) => {
                  const value = event.target.value;
                  setLabelSku(value);
                  setProductSku(value);
                }}
              >
                <option value="">Select SKU</option>
                {(potentialQuery.data ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Product SKU</span>
              <input
                className="w-full rounded border border-slate-200 px-3 py-2"
                value={productSku}
                readOnly
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-700">Art</span>
              <select
                className="w-full rounded border border-slate-200 px-3 py-2"
                value={artId}
                onChange={(event) => setArtId(Number(event.target.value))}
              >
                <option value="">Select art file</option>
                {artOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={
                !labelSku ||
                !productSku ||
                artId === "" ||
                createMutation.isPending ||
                !artOptionsById.has(Number(artId))
              }
              onClick={() =>
                createMutation.mutate({
                  labelSku,
                  productSku,
                  artId: Number(artId),
                })
              }
            >
              {createMutation.isPending ? "Saving…" : "Create label SKU"}
            </Button>
          </div>
        </DocumentModal>
      ) : null}
    </div>
  );
}
